/**
 * Leads - the people behind the accounts: event-funnel sign-ups, contacts
 * added by hand, and business cards photographed in the field.
 *
 * Scanning a card: take or choose a photo -> eMerge keeps it and reads it ->
 * the rep checks every proposed field -> Save makes the lead, owned by them,
 * tied to the facility when they pick one. Nothing becomes a lead unseen.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Camera, Plus, Search, UserPlus } from 'lucide-react';

import { Button } from '../../../components/shadcn/ui/button';
import { Input } from '../../../components/shadcn/ui/input';
import { Textarea } from '../../../components/shadcn/ui/textarea';
import { Account, CardScan, Lead, LeadInput, Meta, bdApi, fmtDate, words } from './bdApi';
import { NativeSelect, StateLine } from './bdUi';

/** How each source reads in the list and the filter. Anything else falls back to words(). */
export const SOURCE_LABEL: Record<string, string> = {
  website: 'Website',
  business_card: 'Business card',
  event: 'Event funnel',
  business_development: 'Added by hand',
};

export default function LeadsView({ meta, onOpen }: { meta: Meta; onOpen: (facilityId: string) => void }) {
  // ?q= opens the list searched - the link in a website-inquiry email carries the visitor's email.
  const [params] = useSearchParams();
  const initialQ = (params.get('q') || '').trim().slice(0, 100);
  const [owner, setOwner] = useState('all');
  const [source, setSource] = useState('');
  const [q, setQ] = useState(initialQ);
  const [query, setQuery] = useState(initialQ);
  const [data, setData] = useState<{ total: number; rows: Lead[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'none' | 'scan' | 'manual'>('none');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const r = await bdApi.leads({ owner, source, q: query, limit: 200 });
    setLoading(false);
    if (r.success) setData(r.data); else setError(r.error || 'Leads could not be loaded.');
  }, [owner, source, query]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => setMode(mode === 'scan' ? 'none' : 'scan')}><Camera className="w-4 h-4 mr-1.5" /> Scan a business card</Button>
        <Button size="sm" variant="outline" onClick={() => setMode(mode === 'manual' ? 'none' : 'manual')}><UserPlus className="w-4 h-4 mr-1.5" /> Add a lead</Button>
      </div>

      {mode === 'scan' && <CardScanner meta={meta} onSaved={() => { setMode('none'); load(); }} onCancel={() => setMode('none')} />}
      {mode === 'manual' && (
        <section className="rounded-lg border p-4">
          <h3 className="text-sm font-semibold mb-3">Add a lead</h3>
          <LeadForm meta={meta} initial={{ first_name: '' }} submitLabel="Save lead"
            onSubmit={(v) => bdApi.addLead(v)} onSaved={() => { setMode('none'); load(); }} onCancel={() => setMode('none')} />
        </section>
      )}

      <form className="flex flex-wrap items-end gap-3" role="search" onSubmit={(e) => { e.preventDefault(); setQuery(q.trim()); }}>
        <NativeSelect label="Owner" value={owner} onChange={setOwner}>
          <option value="all">Everyone</option>
          <option value="mine">Mine</option>
          <option value="unassigned">Unassigned</option>
          {meta.reps.filter((r) => r.id !== meta.me.id).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </NativeSelect>
        <NativeSelect label="Source" value={source} onChange={setSource}>
          <option value="">Any source</option>
          {Object.entries(SOURCE_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </NativeSelect>
        <Input aria-label="Search leads" placeholder="Name, company or email" value={q} onChange={(e) => setQ(e.target.value)} className="w-56" />
        <Button type="submit" size="sm" variant="outline"><Search className="w-4 h-4" /><span className="sr-only">Search</span></Button>
      </form>

      <StateLine loading={loading && !data} error={error} onRetry={load} empty={data && data.rows.length === 0 ? 'No leads match.' : null} />

      {data && data.rows.length > 0 && (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="text-left text-muted-foreground">
                <th className="py-2 px-3 font-medium">Name</th>
                <th className="py-2 px-3 font-medium">Company / facility</th>
                <th className="py-2 px-3 font-medium">Contact</th>
                <th className="py-2 px-3 font-medium">Source</th>
                <th className="py-2 px-3 font-medium">Owner</th>
                <th className="py-2 px-3 font-medium">Added</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((l) => (
                <tr key={l.id} className="border-t align-top">
                  <td className="py-2 px-3">
                    <div className="font-medium">{l.first_name} {l.last_name}</div>
                    {l.title && <div className="text-xs text-muted-foreground">{l.title}</div>}
                  </td>
                  <td className="py-2 px-3">
                    {l.facility_id ? (
                      <button type="button" className="hover:underline text-left" onClick={() => onOpen(l.facility_id as string)}>{l.facility_name}</button>
                    ) : (l.company || <span className="text-muted-foreground">—</span>)}
                  </td>
                  <td className="py-2 px-3 text-xs">
                    {l.email && <div><a className="underline underline-offset-2" href={`mailto:${l.email}`}>{l.email}</a></div>}
                    {l.phone && <div><a className="underline underline-offset-2" href={`tel:${l.phone.replace(/[^\d+]/g, '')}`}>{l.phone}</a></div>}
                  </td>
                  <td className="py-2 px-3 text-xs text-muted-foreground">
                    {SOURCE_LABEL[l.source || ''] || words(l.source || 'unknown')}{l.funnel_event ? ` · ${l.funnel_event}` : ''}{l.funnel_status ? ` · ${words(l.funnel_status)}` : ''}
                  </td>
                  <td className="py-2 px-3">
                    <OwnerSelect lead={l} meta={meta} onChanged={load} />
                  </td>
                  <td className="py-2 px-3 text-xs text-muted-foreground">{fmtDate(l.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.total > data.rows.length && <p className="px-3 py-2 text-xs text-muted-foreground">Showing the newest {data.rows.length} of {data.total}. Search to narrow.</p>}
        </div>
      )}
    </div>
  );
}

function OwnerSelect({ lead, meta, onChanged }: { lead: Lead; meta: Meta; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <NativeSelect label={`Owner of ${lead.first_name} ${lead.last_name}`} hideLabel value={lead.owner_user_id || ''}
      onChange={async (v) => { setBusy(true); await bdApi.updateLead(lead.id, { owner_user_id: v || null }); setBusy(false); onChanged(); }}>
      <option value="">{busy ? 'Saving…' : 'Unassigned'}</option>
      {meta.reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
    </NativeSelect>
  );
}

function CardScanner({ meta, onSaved, onCancel }: { meta: Meta; onSaved: () => void; onCancel: () => void }) {
  const [scan, setScan] = useState<CardScan | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setError(null); setScan(null);
    setPreview(URL.createObjectURL(file));
    setBusy(true);
    const r = await bdApi.scanCard(file);
    setBusy(false);
    if (!r.success) { setError(r.error || 'The photo could not be uploaded.'); return; }
    setScan(r.data);
  };

  const p = scan?.parsed || {};
  const initial: LeadInput = {
    first_name: p.first_name || '', last_name: p.last_name || '', title: p.title || '',
    company: p.company || p.company_hint || '', email: p.email || '', phone: p.phone || p.mobile || '',
    address: p.address || '', city: p.city || '', state: p.state || '', zip: p.zip || '',
    notes: [p.mobile && p.mobile !== p.phone ? `Mobile ${p.mobile}` : '', p.fax ? `Fax ${p.fax}` : '', p.website ? `Web ${p.website}` : '',
      ...(p.unused || [])].filter(Boolean).join('\n'),
    facility_id: scan?.facility_suggestion?.id || null,
  };

  return (
    <section className="rounded-lg border p-4 space-y-3" aria-labelledby="scan-title">
      <h3 id="scan-title" className="text-sm font-semibold">Scan a business card</h3>
      {!meta.card_reading && (
        <p className="text-xs text-muted-foreground">Automatic card reading is not switched on yet - the photo is kept and you type the details.</p>
      )}
      <div className="flex flex-wrap items-start gap-4">
        <div>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="sr-only" aria-label="Photo of a business card"
            onChange={(e) => pick(e.target.files?.[0])} />
          <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy}>
            <Camera className="w-4 h-4 mr-1.5" /> {preview ? 'Retake / choose another' : 'Take or choose a photo'}
          </Button>
          {preview && <img src={preview} alt="The card you photographed" className="mt-3 max-w-[18rem] rounded-md border" />}
        </div>
        <div className="flex-1 min-w-[16rem]">
          {busy && <p role="status" className="text-sm text-muted-foreground">Reading the card…</p>}
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          {scan && (
            <>
              <p role="status" className={`text-xs mb-3 ${scan.ocr_status === 'READ' ? 'text-emerald-700' : 'text-amber-700'}`}>
                {scan.ocr_status === 'READ' ? 'Read the card - check every field before saving.' : scan.reason}
              </p>
              <LeadForm key={scan.id} meta={meta} initial={initial} suggestion={scan.facility_suggestion} submitLabel="Save as lead"
                onSubmit={(v) => bdApi.saveCard(scan.id, v)} onSaved={onSaved} onCancel={onCancel} />
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function LeadForm({
  meta, initial, suggestion, submitLabel, onSubmit, onSaved, onCancel,
}: {
  meta: Meta;
  initial: LeadInput;
  suggestion?: { id: string; name: string; city: string | null } | null;
  submitLabel: string;
  onSubmit: (v: LeadInput) => Promise<{ success: boolean; error?: string; data?: { existed: boolean } }>;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [v, setV] = useState<LeadInput>(initial);
  const [facility, setFacility] = useState<{ id: string; name: string } | null>(suggestion ? { id: suggestion.id, name: suggestion.name } : null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const set = (k: keyof LeadInput) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setV((cur) => ({ ...cur, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    const r = await onSubmit({ ...v, facility_id: facility?.id || null, owner_user_id: v.owner_user_id || meta.me.id });
    setBusy(false);
    if (!r.success) { setMsg(r.error || 'Not saved.'); return; }
    onSaved();
  };

  const field = (k: keyof LeadInput, label: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <label className="flex flex-col gap-1 text-xs text-muted-foreground">
      {label}
      <Input value={(v[k] as string) || ''} onChange={set(k)} {...props} />
    </label>
  );

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        {field('first_name', 'First name', { required: true })}
        {field('last_name', 'Last name')}
        {field('title', 'Title')}
        {field('company', 'Company')}
        {field('email', 'Email', { type: 'email' })}
        {field('phone', 'Phone', { type: 'tel' })}
      </div>
      <div className="grid grid-cols-[1fr_8rem_4rem_6rem] gap-3">
        {field('address', 'Street')}
        {field('city', 'City')}
        {field('state', 'State', { maxLength: 2 })}
        {field('zip', 'ZIP', { maxLength: 10 })}
      </div>
      <FacilityPicker value={facility} onChange={setFacility} />
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        Notes
        <Textarea rows={3} value={v.notes || ''} onChange={set('notes')} />
      </label>
      <NativeSelect label="Owner" value={v.owner_user_id || meta.me.id} onChange={(o) => setV((cur) => ({ ...cur, owner_user_id: o }))}>
        {meta.reps.map((r) => <option key={r.id} value={r.id}>{r.name}{r.id === meta.me.id ? ' (me)' : ''}</option>)}
      </NativeSelect>
      <div className="flex items-center gap-3">
        <Button type="submit" size="sm" disabled={busy || !v.first_name.trim()}><Plus className="w-4 h-4 mr-1" />{busy ? 'Saving…' : submitLabel}</Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
        {msg && <span role="alert" className="text-sm text-destructive">{msg}</span>}
      </div>
    </form>
  );
}

export function FacilityPicker({ value, onChange }: { value: { id: string; name: string } | null; onChange: (v: { id: string; name: string } | null) => void }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Account[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (q.trim().length < 3) { setHits([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const r = await bdApi.accounts({ q, type: 'ALL', limit: 8 });
      setHits(r.success ? r.data.rows : []);
    }, 250);
  }, [q]);

  if (value) {
    return (
      <div className="text-sm flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Facility</span> <b>{value.name}</b>
        <button type="button" className="text-xs underline" onClick={() => onChange(null)}>change</button>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        Facility (optional)
        <Input placeholder="Type a facility name" value={q} onChange={(e) => setQ(e.target.value)} />
      </label>
      {hits.length > 0 && (
        <ul className="rounded-md border bg-background text-sm max-h-48 overflow-auto">
          {hits.map((h) => (
            <li key={h.facility_id}>
              <button type="button" className="w-full text-left px-2 py-1 hover:bg-muted" onClick={() => { onChange({ id: h.facility_id, name: h.name }); setQ(''); }}>
                {h.name} <span className="text-muted-foreground">· {h.city} · {h.facility_type}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
